import { type IAdvancedRichText } from '@framework/engine/inputs/rich-text-advanced/IAdvancedRichText.ts';
import { type IReference } from '@framework/engine/schema/expressions/references/IReference.ts';
import { type IInputWidget } from '@framework/engine/schema/widgets/repository/inputs/IInputWidget.ts';
import { type WidgetType } from '@framework/engine/schema/widgets/Widget.ts';
import { type IMapPrintImage } from './config/print/MapPrintImage.ts';
import { type IMapGroup } from './layers/MapGroup.ts';
import { type IMapLayer, type MapLayerType } from './layers/MapLayer.ts';

declare module '@framework/engine/schema/widgets/Widget.ts' {
  export enum WidgetType {
    Map = 'map',
  }
}

export const WidgetTypeMap = 'map' as WidgetType.Map;

/**
 * Mappa
 */
export interface IMapWidget extends IInputWidget {
  id: string;
  type: WidgetType.Map;
  label?: IAdvancedRichText;
  layout?: IInputWidget['layout'] & { hide?: boolean };
  /**
   * Reference a un tag semantico di tipo Mappa da cui precaricare il valore
   * (geometrie + aree + intersezioni). Usata come valore di default, ad
   * esempio per ripristinare in Back Office la mappa compilata in Front
   * Office.
   */
  defaultValueReference?: IReference | null;
  /** Posizione iniziale della mappa */
  position?: {
    center?: [number, number];
    zoom?: number;
  };
  /** Livelli della mappa (fonti dati a livello grafico) */
  layers?: IMapLayer[];
  /** Gruppi tematici per organizzare i layer nel pannello livelli */
  groups?: IMapGroup[];
  /** Tipo di selezione */
  selection: {
    multiple?: boolean;
    /** @deprecated Utilizzare `shapes`. Migrato automaticamente a v3. */
    shape?: MapWidgetSelectionShape;
    /** Tipologie di geometria selezionabili (supporta geometrie miste) */
    shapes?: MapWidgetSelectionShape[];
    hideGeometriesTable?: boolean;
  };
  /** Distanza di buffer (metri) per geometrie non-poligonali. Default: 5m. */
  bufferDistance?: number;
  /** @deprecated Utilizzare IMapLayer.intersection. Migrato automaticamente da v1. */
  intersection?: {
    title?: string;
    enabled?: boolean;
    type?: MapLayerType;
    url?: string;
    areaTolerance?: number;
  };
  /**
   * @deprecated Utilizzare IMapLayer.competence.
   * Migrato automaticamente da v2.
   */
  competence?: {
    enabled?: boolean;
    type?: MapLayerType;
    url?: string;
    propertyName?: string;
  };
  /** Configurazione Identify (interrogazione layer) */
  identify?: {
    /** Buffer di selezione puntuale in metri (Front Office). Default: 10m */
    bufferDistance?: number;
    /** Buffer di selezione puntuale in metri (Back Office). Default: 10m */
    bufferDistanceBO?: number;
  };
  /** Etichette/numerazione geometrie disegnate */
  labeling?: {
    enabled?: boolean;
    zoomRange?: { min?: number; max?: number };
    prefix?: string;
  };
  /** Gruppi di comuni */
  municipalityGroupList?: string[];
  /** Immagini da stampare */
  printImages?: IMapPrintImage[];
}

export enum MapWidgetSelectionShape {
  Point = 'point',
  Line = 'line',
  Polygon = 'polygon',
}

/**
 * Tipo di geometria OL effettivo, distinto dallo shape di
 * configurazione. Corrisponde ai valori restituiti da
 * `feature.getGeometry().getType()`.
 */
export enum MapGeometryType {
  Point = 'Point',
  LineString = 'LineString',
  Polygon = 'Polygon',
}
